import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useOutletContext } from 'react-router-dom';
import {
  api,
  RoadmapItemWithCounts,
  RoadmapModule,
  RoadmapWorkType,
  RoadmapHorizon,
  RoadmapStatus,
  Branch,
  Insight,
  StrategyMentionStats,
} from '../lib/api';
import { AppOutletContext } from '../App';
import { Tabs } from '../components/Tabs';
import { BranchChip } from '../components/BranchChip';
import { LinkInsightModal } from '../components/LinkInsightModal';
import { FlywheelSummary } from './FlywheelSummary';

// ── Shared constants ──────────────────────────────────────────────────────────

const MODULE_OPTIONS: { value: RoadmapModule; label: string }[] = [
  { value: 'navigator', label: 'Navigator' },
  { value: 'studio', label: 'Studio' },
  { value: 'quant_engine', label: 'Quant Engine' },
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'cross_cutting', label: 'Cross-Cutting' },
  { value: 'platform', label: 'Platform' },
];

const WORK_TYPE_OPTIONS: { value: RoadmapWorkType; label: string }[] = [
  { value: 'r_and_d', label: 'R&D' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'product_ux', label: 'Product/UX' },
  { value: 'mixed', label: 'Mixed' },
];

const HORIZON_OPTIONS: { value: RoadmapHorizon; label: string }[] = [
  { value: 'near_term', label: 'Near-Term' },
  { value: 'mid_term', label: 'Mid-Term' },
  { value: 'long_term', label: 'Long-Term' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'deprioritized', label: 'Deprioritized' },
];

const STATUS_ORDER: RoadmapStatus[] = ['active', 'proposed', 'shipped', 'deprioritized'];

function getStatusClass(status: RoadmapStatus): string {
  switch (status) {
    case 'proposed': return 'tag tag-warning';
    case 'active': return 'tag tag-blocker';
    case 'shipped': return 'tag tag-valid';
    case 'deprioritized': return 'tag tag-unclear';
    default: return 'tag';
  }
}

function formatLabel(value: string): string {
  return value.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// ── Add Item Modal ────────────────────────────────────────────────────────────

interface AddItemModalProps {
  preselectedBranch: Branch | null;
  onClose: () => void;
  onCreated: (item: RoadmapItemWithCounts) => void;
  prefillTitle?: string;
  prefillModule?: RoadmapModule;
  prefillWorkType?: RoadmapWorkType;
}

function AddItemModal({
  preselectedBranch,
  onClose,
  onCreated,
  prefillTitle = '',
  prefillModule = 'navigator',
  prefillWorkType = 'engineering',
}: AddItemModalProps) {
  const [form, setForm] = useState({
    title: prefillTitle,
    description: '',
    module_touched: prefillModule,
    work_type: prefillWorkType,
    horizon: 'near_term' as RoadmapHorizon,
    status: 'proposed' as RoadmapStatus,
    target_ship_date: '',
    owner: '',
    notes: '',
    branch: preselectedBranch,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    setError('');
    try {
      const created = await api.createRoadmapItem({
        title: form.title,
        description: form.description || undefined,
        module_touched: form.module_touched,
        work_type: form.work_type,
        horizon: form.horizon,
        status: form.status,
        target_ship_date: form.target_ship_date || undefined,
        owner: form.owner || undefined,
        notes: form.notes || undefined,
        created_by: undefined,
      });
      // createRoadmapItem returns RoadmapItem; cast with zero counts for local state
      onCreated({ ...created, insight_count: 0, client_count: 0, notified_count: 0, adopted_count: 0 });
      onClose();
    } catch {
      setError('Failed to create roadmap item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add Roadmap Item</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input
              type="text"
              className="form-input"
              value={form.title}
              onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              placeholder="Enter roadmap item title"
              autoFocus
            />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              rows={3}
              value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              placeholder="Optional description"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Module *</label>
              <select className="form-select" value={form.module_touched}
                onChange={e => setForm(p => ({ ...p, module_touched: e.target.value as RoadmapModule }))}>
                {MODULE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Work Type *</label>
              <select className="form-select" value={form.work_type}
                onChange={e => setForm(p => ({ ...p, work_type: e.target.value as RoadmapWorkType }))}>
                {WORK_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Horizon *</label>
              <select className="form-select" value={form.horizon}
                onChange={e => setForm(p => ({ ...p, horizon: e.target.value as RoadmapHorizon }))}>
                {HORIZON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status *</label>
              <select className="form-select" value={form.status}
                onChange={e => setForm(p => ({ ...p, status: e.target.value as RoadmapStatus }))}>
                <option value="proposed">Proposed</option>
                <option value="active">Active</option>
                <option value="shipped">Shipped</option>
                <option value="deprioritized">Deprioritized</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Target Ship Date</label>
              <input type="date" className="form-input" value={form.target_ship_date}
                onChange={e => setForm(p => ({ ...p, target_ship_date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Owner</label>
              <input type="text" className="form-input" value={form.owner}
                onChange={e => setForm(p => ({ ...p, owner: e.target.value }))}
                placeholder="e.g. John Smith" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Branch</label>
            <select className="form-select" value={form.branch ?? ''}
              onChange={e => setForm(p => ({ ...p, branch: (e.target.value as Branch) || null }))}>
              <option value="">Unclassified</option>
              <option value="engineering">Engineering</option>
              <option value="data_science">Data Science</option>
              <option value="finance_economics">Finance &amp; Economics</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" rows={2} value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="Optional notes" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={!form.title.trim() || saving}>
            {saving ? 'Adding...' : 'Add Item'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Demand Evidence Panel (Finance column) ────────────────────────────────────

function DemandEvidencePanel() {
  const [open, setOpen] = useState(false);
  const [stats, setStats] = useState<StrategyMentionStats | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || stats) return;
    setLoading(true);
    api.getStrategyMentionStats().then(s => { setStats(s); setLoading(false); }).catch(() => setLoading(false));
  }, [open, stats]);

  const topStrategies = stats
    ? Object.entries(stats.by_strategy)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
    : [];

  const endClientCount = stats?.end_client_demand_count ?? 0;
  const total = stats?.total_mentions ?? 0;
  const endClientShare = total > 0 ? Math.round((endClientCount / total) * 100) : 0;

  return (
    <div className="demand-evidence-panel">
      <button className="demand-evidence-toggle" onClick={() => setOpen(o => !o)}>
        <span>{open ? '▾' : '▸'} Demand evidence</span>
        {stats && <span className="text-muted" style={{ fontSize: '0.75rem' }}>{total} mentions</span>}
      </button>
      {open && (
        <div className="demand-evidence-body">
          {loading && <div className="text-muted" style={{ fontSize: '0.8125rem' }}>Loading...</div>}
          {!loading && stats && (
            <>
              <div style={{ marginBottom: '0.75rem' }}>
                {topStrategies.map(([strategy, count]) => (
                  <div key={strategy} className="demand-evidence-row">
                    <span className="demand-evidence-name">{formatLabel(strategy)}</span>
                    <span className="demand-evidence-count">{count}</span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                End-client share: {endClientShare}%
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <Link to="/patterns/demand" className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}>
                  Demand over time
                </Link>
                <Link to="/patterns/landscape" className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}>
                  Strategy landscape
                </Link>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Board Tab ─────────────────────────────────────────────────────────────────

const COLUMNS: { branch: Branch; label: string }[] = [
  { branch: 'engineering', label: 'Engineering' },
  { branch: 'data_science', label: 'Data Science' },
  { branch: 'finance_economics', label: 'Finance & Economics' },
];

function sortItems(items: RoadmapItemWithCounts[]): RoadmapItemWithCounts[] {
  return [...items].sort((a, b) => {
    const statusA = STATUS_ORDER.indexOf(a.status);
    const statusB = STATUS_ORDER.indexOf(b.status);
    if (statusA !== statusB) return statusA - statusB;
    return b.insight_count - a.insight_count;
  });
}

interface BoardTabProps {
  items: RoadmapItemWithCounts[];
  onItemCreated: (item: RoadmapItemWithCounts) => void;
}

function BoardTab({ items, onItemCreated }: BoardTabProps) {
  const navigate = useNavigate();
  const [addModalBranch, setAddModalBranch] = useState<Branch | null | 'unclassified' | null>(null);

  const unclassified = sortItems(items.filter(i => !i.branch));
  const hasUnclassified = unclassified.length > 0;

  return (
    <div>
      {hasUnclassified && (
        <div className="roadmap-unclassified-strip">
          <div className="roadmap-unclassified-label">Unclassified ({unclassified.length})</div>
          <div className="roadmap-unclassified-items">
            {unclassified.map(item => (
              <div key={item.roadmap_item_id} className="card-glass roadmap-unclassified-card"
                onClick={() => navigate(`/roadmap/${item.roadmap_item_id}`)}>
                <span className="insight-text" style={{ fontSize: '0.875rem' }}>{item.title}</span>
                <span className={getStatusClass(item.status)} style={{ marginLeft: 'auto', flexShrink: 0 }}>
                  {formatLabel(item.status)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="roadmap-board">
        {COLUMNS.map(col => {
          const colItems = sortItems(items.filter(i => i.branch === col.branch));
          return (
            <div key={col.branch} className="roadmap-column">
              <div className="roadmap-column-header">
                <h3 className="roadmap-column-title">{col.label}</h3>
                <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}
                  onClick={() => setAddModalBranch(col.branch)}>
                  + Add
                </button>
              </div>
              <div className="roadmap-column-items">
                {colItems.length === 0 && (
                  <div className="text-muted" style={{ fontSize: '0.8125rem', textAlign: 'center', padding: '1.5rem 0' }}>
                    No items
                  </div>
                )}
                {colItems.map(item => (
                  <div key={item.roadmap_item_id} className="card-glass roadmap-card"
                    onClick={() => navigate(`/roadmap/${item.roadmap_item_id}`)}>
                    <div className="roadmap-card-title">{item.title}</div>
                    <div className="roadmap-card-meta">
                      <span className={getStatusClass(item.status)}>{formatLabel(item.status)}</span>
                      <div className="roadmap-card-counts">
                        <span className="roadmap-card-count">{item.insight_count} insights</span>
                        <span className="roadmap-card-count">{item.client_count} clients</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {col.branch === 'finance_economics' && (
                <DemandEvidencePanel />
              )}
            </div>
          );
        })}
      </div>

      {addModalBranch !== null && (
        <AddItemModal
          preselectedBranch={addModalBranch === 'unclassified' ? null : addModalBranch as Branch}
          onClose={() => setAddModalBranch(null)}
          onCreated={item => { onItemCreated(item); setAddModalBranch(null); }}
        />
      )}
    </div>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────

interface ToastState {
  message: string;
  onUndo: () => void;
  timerId: ReturnType<typeof setTimeout>;
}

// ── Link Modal ────────────────────────────────────────────────────────────────
// Delegated to shared component; local alias keeps TriageTab call-sites unchanged.

interface LinkModalProps {
  insight: Insight;
  currentUserId: string | undefined;
  onLinked: (roadmapItemId: string, roadmapItemTitle: string) => void;
  onClose: () => void;
}

function LinkModal({ insight, currentUserId, onLinked, onClose }: LinkModalProps) {
  return (
    <LinkInsightModal
      insight={insight}
      currentUserId={currentUserId}
      onLinked={onLinked}
      onClose={onClose}
    />
  );
}

// ── Reject Modal ──────────────────────────────────────────────────────────────

interface RejectModalProps {
  insight: Insight;
  onRejected: (reason: string) => void;
  onClose: () => void;
}

const QUICK_PICKS = [
  'Out of scope',
  'Duplicate',
  'Declined by design',
];

function RejectModal({ insight, onRejected, onClose }: RejectModalProps) {
  const [reason, setReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [error, setError] = useState('');

  const handleReject = async () => {
    if (!reason.trim()) return;
    setRejecting(true);
    setError('');
    try {
      await api.setInsightDisposition(insight.insight_id, { disposition: 'rejected', rejection_reason: reason.trim() });
      onRejected(reason.trim());
    } catch {
      setError('Failed to reject insight');
      setRejecting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Reject Insight</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}
          <div className="text-small text-muted" style={{ marginBottom: '1rem', borderLeft: '2px solid var(--glass-border)', paddingLeft: '0.75rem' }}>
            {insight.insight_text.length > 120 ? insight.insight_text.slice(0, 120) + '...' : insight.insight_text}
          </div>
          <div className="form-group">
            <label className="form-label">Quick picks</label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              {QUICK_PICKS.map(pick => (
                <button key={pick}
                  className={`filter-tab${reason === pick ? ' active' : ''}`}
                  onClick={() => setReason(pick)}>
                  {pick}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Reason *</label>
            <textarea className="form-textarea" rows={3} value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Required — explain why this insight is rejected" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={rejecting}>Cancel</button>
          <button className="btn btn-primary" onClick={handleReject}
            disabled={!reason.trim() || rejecting}>
            {rejecting ? 'Rejecting...' : 'Confirm Reject'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Triage Tab ────────────────────────────────────────────────────────────────

type BranchFilter = Branch | 'all' | 'unclassified';

interface TriageTabProps {
  currentUserId: string | undefined;
  onBadgeRefresh: () => void;
}

function TriageTab({ currentUserId, onBadgeRefresh }: TriageTabProps) {
  const [branchFilter, setBranchFilter] = useState<BranchFilter>('all');
  const [items, setItems] = useState<Insight[]>([]);
  const [counts, setCounts] = useState({ engineering: 0, data_science: 0, finance_economics: 0, unclassified: 0 });
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [showLink, setShowLink] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);
  // Track removed items for undo: { item, index }
  const removedRef = useRef<{ item: Insight; idx: number } | null>(null);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const branch = branchFilter === 'all' ? undefined
        : branchFilter === 'unclassified' ? undefined
        : branchFilter as Branch;
      const result = await api.getTriageQueue({ branch, limit: 50 });
      let insights = result.insights;
      if (branchFilter === 'unclassified') {
        insights = insights.filter(i => !i.branch);
      }
      setItems(insights);
      setCounts(result.counts);
      setTotal(result.total);
      setSelectedIdx(0);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [branchFilter]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  // Keyboard navigation — only when no modal is open and no input focused
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (showLink || showReject) return;
      if (e.key === 'j') setSelectedIdx(i => Math.min(i + 1, items.length - 1));
      else if (e.key === 'k') setSelectedIdx(i => Math.max(i - 1, 0));
      else if (e.key === 'l') { if (items[selectedIdx]) setShowLink(true); }
      else if (e.key === 'r') { if (items[selectedIdx]) setShowReject(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [items, selectedIdx, showLink, showReject]);

  const clearToast = (t: ToastState) => {
    clearTimeout(t.timerId);
    setToast(null);
  };

  const showUndoToast = (message: string, onUndo: () => void) => {
    if (toast) clearTimeout(toast.timerId);
    const timerId = setTimeout(() => setToast(null), 5000);
    setToast({ message, onUndo, timerId });
  };

  const optimisticallyRemove = (insight: Insight) => {
    setItems(prev => {
      const idx = prev.findIndex(i => i.insight_id === insight.insight_id);
      removedRef.current = { item: insight, idx };
      return prev.filter(i => i.insight_id !== insight.insight_id);
    });
    setSelectedIdx(i => Math.max(0, i > 0 ? i - 1 : 0));
    onBadgeRefresh();
  };

  const undoRemove = () => {
    if (!removedRef.current) return;
    const { item, idx } = removedRef.current;
    setItems(prev => {
      const next = [...prev];
      next.splice(idx, 0, item);
      return next;
    });
    removedRef.current = null;
    onBadgeRefresh();
  };

  const handleLinked = async (roadmapItemId: string, roadmapItemTitle: string) => {
    const insight = items[selectedIdx];
    setShowLink(false);
    optimisticallyRemove(insight);
    const captured = { insight_id: insight.insight_id, roadmapItemId };
    showUndoToast(`Linked to "${roadmapItemTitle}" — Undo`, async () => {
      try {
        await api.unlinkInsightFromRoadmap(captured.roadmapItemId, captured.insight_id);
        await api.setInsightDisposition(captured.insight_id, { disposition: 'pending' });
        undoRemove();
      } catch { /* silent */ }
      onBadgeRefresh();
    });
  };

  const handleRejected = async (_reason: string) => {
    const insight = items[selectedIdx];
    setShowReject(false);
    optimisticallyRemove(insight);
    const captured = { insight_id: insight.insight_id };
    showUndoToast(`Rejected — Undo`, async () => {
      try {
        await api.setInsightDisposition(captured.insight_id, { disposition: 'pending' });
        undoRemove();
      } catch { /* silent */ }
      onBadgeRefresh();
    });
  };

  const handleBranchChange = async (insight: Insight, branch: Branch | null) => {
    try {
      await api.updateInsightBranch(insight.insight_id, branch);
      setItems(prev => prev.map(i => i.insight_id === insight.insight_id ? { ...i, branch } : i));
    } catch { /* silent */ }
  };

  const BRANCH_FILTERS: { id: BranchFilter; label: string; count: number }[] = [
    { id: 'all', label: 'All', count: total },
    { id: 'engineering', label: 'Engineering', count: counts.engineering },
    { id: 'data_science', label: 'Data Science', count: counts.data_science },
    { id: 'finance_economics', label: 'Finance & Econ', count: counts.finance_economics },
    { id: 'unclassified', label: 'Unclassified', count: counts.unclassified },
  ];

  const selectedInsight = items[selectedIdx] ?? null;

  return (
    <div>
      {/* Branch filter chips */}
      <div className="filter-tabs" style={{ marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        {BRANCH_FILTERS.map(f => (
          <button key={f.id}
            className={`filter-tab${branchFilter === f.id ? ' active' : ''}`}
            onClick={() => setBranchFilter(f.id)}>
            {f.label}
            {f.count > 0 && <span style={{ marginLeft: '0.375rem', opacity: 0.7 }}>({f.count})</span>}
          </button>
        ))}
      </div>

      {loading && <div className="loading">Loading...</div>}

      {!loading && items.length === 0 && (
        <div style={{ textAlign: 'center', padding: '3rem 0', color: 'var(--text-muted)' }}>
          No pending insights in triage
        </div>
      )}

      {!loading && items.length > 0 && (
        <div>
          {items.map((insight, idx) => (
            <div key={insight.insight_id}
              className={`card-glass triage-item${idx === selectedIdx ? ' selected' : ''}`}
              onClick={() => setSelectedIdx(idx)}>
              <div className="triage-item-text">{insight.insight_text}</div>
              {insight.supporting_quote && (
                <div className="triage-item-quote">"{insight.supporting_quote}"</div>
              )}
              <div className="triage-item-meta">
                <span className="text-muted" style={{ fontSize: '0.8125rem' }}>
                  {insight.client_name}
                  {insight.meeting_date && ` · ${insight.meeting_date}`}
                  {insight.transcript_id && (
                    <>
                      {' · '}
                      <Link
                        to={`/clients/${insight.client_id}`}
                        onClick={e => e.stopPropagation()}
                        style={{ color: 'var(--brand-purple)' }}>
                        View transcript
                      </Link>
                    </>
                  )}
                </span>
                <BranchChip
                  branch={insight.branch}
                  editable
                  onChange={branch => handleBranchChange(insight, branch)}
                />
              </div>
              {idx === selectedIdx && (
                <div className="triage-item-actions">
                  <button className="btn btn-secondary" onClick={e => { e.stopPropagation(); setShowLink(true); }}>
                    Link
                  </button>
                  <button className="btn btn-secondary" onClick={e => { e.stopPropagation(); setShowReject(true); }}>
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Keyboard hint */}
      {items.length > 0 && (
        <div className="triage-keyboard-hint">
          j/k navigate · l link · r reject
        </div>
      )}

      {/* Modals */}
      {showLink && selectedInsight && (
        <LinkModal
          insight={selectedInsight}
          currentUserId={currentUserId}
          onLinked={handleLinked}
          onClose={() => setShowLink(false)}
        />
      )}
      {showReject && selectedInsight && (
        <RejectModal
          insight={selectedInsight}
          onRejected={handleRejected}
          onClose={() => setShowReject(false)}
        />
      )}

      {/* Undo toast */}
      {toast && (
        <div className="undo-toast card-glass">
          <span>{toast.message}</span>
          <button className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.25rem 0.75rem' }}
            onClick={() => { toast.onUndo(); clearToast(toast); }}>
            Undo
          </button>
          <button style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', marginLeft: '0.25rem' }}
            onClick={() => clearToast(toast)}>&times;</button>
        </div>
      )}
    </div>
  );
}

// ── Main Roadmap Page ─────────────────────────────────────────────────────────

export function Roadmap() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser, badges, refreshBadges } = useOutletContext<AppOutletContext>();

  const tab = (searchParams.get('tab') ?? 'board') as 'board' | 'triage' | 'narrative';

  const [boardItems, setBoardItems] = useState<RoadmapItemWithCounts[]>([]);
  const [boardLoading, setBoardLoading] = useState(false);

  // Fetch board items once (or when needed)
  useEffect(() => {
    if (tab !== 'board') return;
    if (boardItems.length > 0) return;
    setBoardLoading(true);
    api.listRoadmapItems({ limit: 200 })
      .then(r => { setBoardItems(r.roadmap_items); setBoardLoading(false); })
      .catch(() => setBoardLoading(false));
  }, [tab]);

  const handleTabChange = (id: string) => {
    setSearchParams({ tab: id }, { replace: true });
  };

  const handleItemCreated = (item: RoadmapItemWithCounts) => {
    setBoardItems(prev => [item, ...prev]);
  };

  const tabs = [
    { id: 'board', label: 'Board' },
    { id: 'triage', label: 'Triage', badge: badges.triage_pending || undefined },
    { id: 'narrative', label: 'Narrative' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Roadmap</h1>
        <p className="page-subtitle">Three-branch board, triage queue, and flywheel narrative</p>
      </div>

      <Tabs tabs={tabs} active={tab} onChange={handleTabChange} />

      <div style={{ marginTop: '1.5rem' }}>
        {tab === 'board' && (
          boardLoading
            ? <div className="loading">Loading...</div>
            : <BoardTab items={boardItems} onItemCreated={handleItemCreated} />
        )}
        {tab === 'triage' && (
          <TriageTab
            currentUserId={currentUser?.user_id}
            onBadgeRefresh={refreshBadges}
          />
        )}
        {tab === 'narrative' && <FlywheelSummary />}
      </div>
    </div>
  );
}
